import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import { LandingPage } from "@/pages/LandingPage";
import { AuthCallback } from "@/pages/AuthCallback";
import { Dashboard } from "@/pages/Dashboard";
import { NewConsultation } from "@/pages/NewConsultation";
import { Payment } from "@/pages/Payment";
import { Questions } from "@/pages/Questions";
import { Diagnosis } from "@/pages/Diagnosis";
import { PanchkarmaCenters } from "@/pages/PanchkarmaCenters";
import { Booking } from "@/pages/Booking";
import { ConsultationReport } from "@/pages/ConsultationReport";
import { AdminPanel } from "@/pages/AdminPanel";
import { AdminPortal } from "@/pages/AdminPortal";
import { FeedbackPage } from "@/pages/FeedbackPage";
import { PrivacyPolicy } from "@/pages/PrivacyPolicy";
import { Disclaimer } from "@/pages/Disclaimer";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Protected Route Component - checks both context and localStorage
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Check localStorage as backup
  const hasToken = !!localStorage.getItem('session_token');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(40,23%,96%)]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Allow access if authenticated OR if we have a token (user state might be syncing)
  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (adminOnly && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consultation/new"
              element={
                <ProtectedRoute>
                  <NewConsultation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consultation/:id/payment"
              element={
                <ProtectedRoute>
                  <Payment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consultation/:id/questions"
              element={
                <ProtectedRoute>
                  <Questions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consultation/:id/diagnosis"
              element={
                <ProtectedRoute>
                  <Diagnosis />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consultation/:id/report"
              element={
                <ProtectedRoute>
                  <ConsultationReport />
                </ProtectedRoute>
              }
            />
            <Route path="/panchkarma" element={<PanchkarmaCenters />} />
            <Route
              path="/booking/:consultationId/:centerId"
              element={
                <ProtectedRoute>
                  <Booking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route path="/admin-portal" element={<AdminPortal />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route
              path="/feedback/:consultationId"
              element={
                <ProtectedRoute>
                  <FeedbackPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
